import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight, Award, BookOpen, Check, ChevronDown, Compass, FileText, Globe2, GraduationCap,
  Megaphone, Menu, Plane, Search, Send, ShieldCheck, Sparkles, Stamp, Star, Wallet, X, MessageCircle, Upload
} from "lucide-react";
import "@/App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API, withCredentials: true });

const destinations = [
  ["United Kingdom", "London, Manchester & beyond", "https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?auto=format&fit=crop&w=900&q=80", "Business, Computing, Health", "£12k–£28k"],
  ["Germany", "Berlin, Munich & Hamburg", "https://images.unsplash.com/photo-1467269204594-9661b134dd2b?auto=format&fit=crop&w=900&q=80", "Engineering, Data, Business", "€0–€15k"],
  ["Finland", "Helsinki & Tampere", "https://images.unsplash.com/photo-1519501025264-65ba15a82390?auto=format&fit=crop&w=900&q=80", "Technology, Education, Design", "€8k–€14k"],
  ["Italy", "Rome, Milan & Bologna", "https://images.unsplash.com/photo-1529260830199-42c24126f198?auto=format&fit=crop&w=900&q=80", "Architecture, Arts, Business", "€2k–€10k"],
  ["Australia", "Sydney, Melbourne & Adelaide", "https://images.unsplash.com/photo-1523482580672-f109ba8cb9be?auto=format&fit=crop&w=900&q=80", "IT, Engineering, Nursing", "A$25k–A$45k"],
  ["Canada", "Toronto, Vancouver & Calgary", "https://images.unsplash.com/photo-1517935706615-2717063c2225?auto=format&fit=crop&w=900&q=80", "Business, Health, Computing", "C$18k–C$35k"],
  ["Cyprus", "Nicosia & Limassol", "https://images.unsplash.com/photo-1530789253388-582c481c54b0?auto=format&fit=crop&w=900&q=80", "Business, Hospitality", "€6k–€12k"],
  ["Turkey", "Istanbul & Ankara", "https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?auto=format&fit=crop&w=900&q=80", "Medicine, Engineering", "$4k–$15k"],
  ["Ireland", "Dublin, Cork & Galway", "https://images.unsplash.com/photo-1518635017498-87f514b751ba?auto=format&fit=crop&w=900&q=80", "Tech, Pharma, Business", "€10k–€22k"],
  ["Other Europe", "Netherlands, Sweden, Spain", "https://images.unsplash.com/photo-1467269204594-9661b134dd2b?auto=format&fit=crop&w=900&q=80", "Wide range of fields", "€3k–€18k"]
];

const services = [
  { name: "University Selection", Icon: GraduationCap },
  { name: "Admission Guidance", Icon: FileText },
  { name: "Application Processing", Icon: Send },
  { name: "Scholarship Guidance", Icon: Award },
  { name: "IELTS / PTE Guidance", Icon: BookOpen },
  { name: "Student Visa Guidance", Icon: Stamp },
  { name: "Financial Documentation", Icon: Wallet },
  { name: "Pre-Departure Guidance", Icon: Plane }
];

const stages = [
  "Profile Submitted", "Documents Under Review", "University Selection",
  "Application Submitted", "Offer Received", "Visa Process", "Completed"
];

const NEXTON = {
  address: "Saleemi Chowk Basement, MCB Bank, Satayana Rd, Suhail Abad Batala Colony, Faisalabad, 38000, Pakistan",
  short_address: "Saleemi Chowk, Satayana Road, Faisalabad",
  city: "Faisalabad, Pakistan",
  phone_display: "+92 300 1792280",
  phone_dial: "+923001792280",
  whatsapp: "923001792280",
  email: "nextonedu@gmail.com",
  map_embed:
    "https://maps.google.com/maps?q=Nexton%20Education%20Saleemi%20Chowk%20Satayana%20Road%20Faisalabad&t=&z=16&ie=UTF8&iwloc=&output=embed",
  map_link: "https://maps.app.goo.gl/hZf7idWFWRXi5T91A"
};

const SOCIALS = [
  {
    name: "Facebook",
    url: "https://facebook.com/nextoneducation",
    color: "#1877F2",
    svg: (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
        <path d="M24 12.073C24 5.446 18.627 0 12 0S0 5.446 0 12.073c0 6.019 4.388 11.005 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953h-1.513c-1.49 0-1.955.925-1.955 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.078 24 18.092 24 12.073Z" />
      </svg>
    )
  },
  {
    name: "Instagram",
    url: "https://instagram.com/nextoneducation",
    gradient: "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
    color: "#fff",
    svg: (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.334 3.608 1.308.975.975 1.246 2.242 1.308 3.608.058 1.266.070 1.646.070 4.850s-.012 3.584-.070 4.850c-.062 1.366-.334 2.633-1.308 3.608-.975.975-2.242 1.246-3.608 1.308-1.266.058-1.646.070-4.850.070s-3.584-.012-4.850-.070c-1.366-.062-2.633-.334-3.608-1.308-.975-.975-1.246-2.242-1.308-3.608-.058-1.266-.070-1.646-.070-4.850s.012-3.584.070-4.850c.062-1.366.334-2.633 1.308-3.608.975-.975 2.242-1.246 3.608-1.308 1.266-.058 1.646-.070 4.850-.070zM12 0C8.741 0 8.332.014 7.053.072 5.775.132 4.687.396 3.678 1.405 2.669 2.414 2.405 3.502 2.345 4.780.014 8.332 0 8.741 0 12s.014 3.668.072 4.947c.060 1.278.324 2.366 1.333 3.375 1.009 1.009 2.097 1.273 3.375 1.333C8.332 21.986 8.741 22 12 22s3.668-.014 4.947-.072c1.278-.060 2.366-.324 3.375-1.333 1.009-1.009 1.273-2.097 1.333-3.375.058-1.279.072-1.688.072-4.947s-.014-3.668-.072-4.947c-.060-1.278-.324-2.366-1.333-3.375C19.313.396 18.225.132 16.947.072 15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
      </svg>
    )
  },
  {
    name: "TikTok",
    url: "https://tiktok.com/@nextoneducation",
    color: "#000",
    svg: (
      <svg viewBox="0 0 24 24" width="18" height="18">
        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005.86 20.1a6.34 6.34 0 0010.86-4.43V7.83a8.16 8.16 0 004.77 1.52V5.9a4.85 4.85 0 01-1.9-.21z" fill="#ff0050"/>
        <path d="M17.69 4.9a4.83 4.83 0 01-3.77-4.25V.21h-3.45V13.88a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V7.61a6.84 6.84 0 00-1-.05A6.33 6.33 0 003.96 18.31a6.34 6.34 0 0010.86-4.43V6.04a8.16 8.16 0 004.77 1.52V4.11a4.85 4.85 0 01-1.9-.21z" fill="#00f2ea"/>
        <path d="M18.64 5.79a4.83 4.83 0 01-3.77-4.25V1.1h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V8.5a6.84 6.84 0 00-1-.05A6.33 6.33 0 004.91 19.2a6.34 6.34 0 0010.86-4.43V6.93a8.16 8.16 0 004.77 1.52V5a4.85 4.85 0 01-1.9-.21z" fill="#000"/>
      </svg>
    )
  },
  {
    name: "WhatsApp",
    url: `https://wa.me/${NEXTON.whatsapp}`,
    color: "#25D366",
    svg: (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413" />
      </svg>
    )
  }
];

/* ---------- SHELL ---------- */
function Shell({ children }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const doSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/universities?search=${encodeURIComponent(q.trim())}`);
  };

  return (
    <>
      <header className="nav">
        <Link to="/" className="brand" data-testid="brand-link">
          <img src="/brand/nexton-logo.png" alt="Nexton Education" className="brand-logo" />
          <span>Nexton<small>Education Consultant</small></span>
        </Link>

        <form className="nav-search" onSubmit={doSearch} data-testid="global-search-form">
          <Search size={16} />
          <input
            data-testid="global-search-input"
            placeholder="Search universities, programs or destinations…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </form>

        <button className="menu-button" data-testid="mobile-menu-button" onClick={() => setOpen(!open)}>
          {open ? <X /> : <Menu />}
        </button>

        <nav className={open ? "nav-links open" : "nav-links"}>
          {[
            ["/", "Home"],
            ["/destinations", "Destinations"],
            ["/universities", "Universities"],
            ["/programs", "Programs"],
            ["/scholarships", "Scholarships"],
            ["/about", "About"],
            ["/contact", "Contact"]
          ].map(([to, label]) => (
            <Link key={to} to={to} data-testid={`nav-${label.toLowerCase()}`} onClick={() => setOpen(false)}>
              {label}
            </Link>
          ))}
          <Link className="nav-apply" to="/apply" data-testid="nav-apply" onClick={() => setOpen(false)}>
            <svg
              className="apply-icon"
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path
                className="apply-trail"
                d="M2.5 20.5 C 7 18, 11 15, 14 10.5"
                strokeDasharray="1.6 2.6"
                opacity="0.85"
              />
              <path
                className="apply-star"
                d="M18 3.3 l1.75 3.55 l3.9 .58 l-2.82 2.75 l.67 3.9 l-3.5 -1.84 l-3.5 1.84 l.67 -3.9 l-2.82 -2.75 l3.9 -.58 Z"
                fill="currentColor"
                stroke="none"
              />
              <circle cx="2.5" cy="20.5" r="1" fill="currentColor" stroke="none" />
            </svg>
            Apply now
          </Link>
        </nav>
      </header>

      {children}

      <a
        className="whatsapp"
        href={`https://wa.me/${NEXTON.whatsapp}?text=${encodeURIComponent("Hi Nexton, I want to know more about studying abroad.")}`}
        target="_blank"
        rel="noreferrer"
        aria-label="Chat with Nexton on WhatsApp"
        data-testid="whatsapp-button"
      >
        <MessageCircle size={20} />
        <span className="whatsapp-label">WhatsApp us</span>
      </a>

      <footer>
        <div>
          <div className="brand footer-brand">
            <img src="/brand/nexton-logo.png" alt="Nexton Education" className="brand-logo footer-logo" />
            <span>Nexton<small>Education Consultant</small></span>
          </div>
          <p>Guiding your journey, building your future.</p>
          <p className="footer-disclaimer">
            Nexton is an independent education consultancy. University names appearing on this site
            are used for identification only and do not imply affiliation or endorsement.
          </p>
          <div className="footer-socials" data-testid="footer-socials">
            {SOCIALS.map((s) => (
              <a
                key={s.name}
                href={s.url}
                target="_blank"
                rel="noreferrer"
                aria-label={s.name}
                data-testid={`social-${s.name.toLowerCase()}`}
                className="social-icon"
                style={{ background: s.gradient || s.color, color: s.color === "#000" ? "#fff" : "#fff" }}
              >
                {s.svg}
              </a>
            ))}
          </div>
        </div>
        <div>
          <strong>Explore</strong>
          <Link to="/destinations">Study destinations</Link>
          <Link to="/universities">Find a university</Link>
          <Link to="/apply">Student application</Link>
          <Link to="/scholarships">Scholarships</Link>
        </div>
        <div>
          <strong>Talk to us</strong>
          <a href={`tel:${NEXTON.phone_dial}`}>{NEXTON.phone_display}</a>
          <a href={`mailto:${NEXTON.email}`}>{NEXTON.email}</a>
          <span>{NEXTON.short_address}</span>
        </div>
      </footer>
    </>
  );
}

/* ---------- BUTTON ---------- */
function Button({ to, children, secondary = false, onClick }) {
  const cls = secondary ? "button secondary" : "button";
  const testId = `button-${String(children).toLowerCase().replaceAll(" ", "-")}`;
  if (to) {
    return (
      <Link to={to} className={cls} data-testid={testId}>
        {children}
        <ArrowRight size={17} />
      </Link>
    );
  }
  return (
    <button onClick={onClick} className={cls} data-testid={testId}>
      {children}
      <ArrowRight size={17} />
    </button>
  );
}

/* ---------- PAGE INTRO ---------- */
function PageIntro({ label, title, children }) {
  return (
    <section className="page-intro">
      <div className="section-label">{label}</div>
      {title && <h1>{title}</h1>}
      {children}
    </section>
  );
}

/* ---------- HOME ---------- */
function Home() {
  return (
    <Shell>
      <main>
        <section className="hero">
          <div className="hero-copy">
            <div className="eyebrow" data-testid="hero-eyebrow">
              <Sparkles size={14} /> Pakistan's study abroad partner
            </div>
            <h1>Build a future<br /><em>without borders.</em></h1>
            <p>
              Your trusted partner for studying abroad. We help students choose the right destination,
              university and program — then guide you through every step of admission and visa.
            </p>
            <div className="hero-actions">
              <Button to="/destinations">Explore destinations</Button>
              <Button to="/apply" secondary>Apply now</Button>
            </div>
            <div className="hero-proof">
              <div><strong>12+</strong><span>years of combined team experience</span></div>
              <div><strong>Growing</strong><span>student community</span></div>
              <div><strong>Honest</strong><span>guidance you can trust</span></div>
            </div>
          </div>
          <div className="hero-visual">
            <img
              src="https://images.unsplash.com/photo-1704452607333-f0e189d7e2cc?auto=format&fit=crop&w=1200&q=85"
              alt="Students celebrating graduation"
            />
            <div className="hero-float">
              <span className="float-icon"><Check size={19} /></span>
              <span>
                <strong>Trusted guidance</strong>
                <small>From first choice to arrival</small>
              </span>
            </div>
            <div className="hero-stamp">
              <Globe2 size={18} />
              <span>
                <strong>10</strong>
                <small>destinations we guide to</small>
              </span>
            </div>
          </div>
        </section>

        <section className="section intro">
          <div className="section-label">What we do</div>
          <div className="section-heading">
            <h2>A clearer path to<br /><em>your next chapter.</em></h2>
            <p>
              Good advice changes everything. Our counselors combine destination knowledge, university
              insight and genuine care to help you make a confident move.
            </p>
          </div>
          <div className="service-grid">
            {services.map((s, i) => (
              <div className="service" key={s.name} data-testid={`service-${i}`}>
                <div className="service-head">
                  <span className="service-num">0{i + 1}</span>
                  <div className="service-icon">
                    <s.Icon size={22} />
                  </div>
                </div>
                <h3>{s.name}</h3>
                <ArrowRight size={18} />
              </div>
            ))}
          </div>
        </section>

        <section className="dark-band">
          <div className="section-label gold">Where will you go?</div>
          <div className="band-head">
            <h2>Find your <em>place</em> in the world.</h2>
            <Button to="/destinations" secondary>See all destinations</Button>
          </div>
          <div className="destination-strip">
            {destinations.slice(0, 3).map((d) => (
              <Link
                to="/destinations"
                className="destination-mini"
                key={d[0]}
                data-testid={`home-destination-${d[0].toLowerCase().replace(/\s/g, "-")}`}
              >
                <img src={d[2]} alt={d[0]} />
                <div>
                  <span>{d[0]}</span>
                  <small>{d[3]}</small>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="section trust">
          <div className="trust-copy">
            <div className="section-label">Why Nexton</div>
            <h2>Big decisions deserve<br /><em>human guidance.</em></h2>
            <p>
              We're not just a form-filling service. We learn your ambitions, find the right fit
              and stay beside you until you're settled into your new life.
            </p>
            <Button to="/about">Meet Nexton</Button>
          </div>
          <div className="trust-list">
            {[
              "One-on-one counselor support",
              "Transparent fees, no surprises",
              "Complete visa & document guidance",
              "A community that stays connected"
            ].map((x, i) => (
              <div key={x} data-testid={`trust-point-${i}`}>
                <span>0{i + 1}</span>
                <strong>{x}</strong>
                <Check size={18} />
              </div>
            ))}
          </div>
        </section>
      </main>
    </Shell>
  );
}

/* ---------- DESTINATIONS ---------- */
function Destinations() {
  return (
    <Shell>
      <PageIntro
        label="Study destinations"
        title={<>The world is waiting.<br /><em>Let's find your fit.</em></>}
      >
        <div className="filter-line">
          <span>Popular with Pakistani students</span>
          <span>10 destinations <ChevronDown size={16} /></span>
        </div>
      </PageIntro>
      <div className="destination-grid">
        {destinations.map((d, i) => (
          <article className="destination-card" key={d[0]} data-testid={`destination-card-${i}`}>
            <img src={d[2]} alt={d[0]} />
            <div className="destination-body">
              <div className="card-kicker">0{i + 1} / DESTINATION</div>
              <h3>{d[0]}</h3>
              <p>{d[1]}</p>
              <div className="tags">
                <span>{d[3]}</span>
                <span>{d[4]}</span>
              </div>
              <div className="card-actions">
                <Link to={`/universities?country=${encodeURIComponent(d[0])}`} data-testid={`destination-universities-${i}`}>
                  View universities <ArrowRight size={15} />
                </Link>
                <Link to="/apply" data-testid={`destination-apply-${i}`}>Get guidance</Link>
              </div>
            </div>
          </article>
        ))}
      </div>
    </Shell>
  );
}

/* ---------- UNIVERSITIES ---------- */
// Clean branded placeholder (no random stock photos). If a university has a real image URL it is used instead.
function UniImage({ u }) {
  const [failed, setFailed] = useState(false);
  if (u.image && !failed) {
    return <img src={u.image} alt={u.name} loading="lazy" onError={() => setFailed(true)} />;
  }
  const initials = u.name
    .replace(/\b(of|the|and|di|de)\b/gi, "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
  return (
    <div className="uni-placeholder" role="img" aria-label={u.name}>
      <span className="uni-initials">{initials}</span>
      <small>{u.city}</small>
    </div>
  );
}

function Universities() {
  const [items, setItems] = useState([]);
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get("search") || "");
  const [country, setCountry] = useState(params.get("country") || "");

  useEffect(() => {
    api
      .get("/universities", { params: { search, country } })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
    const next = {};
    if (search) next.search = search;
    if (country) next.country = country;
    setParams(next, { replace: true });
  }, [search, country, setParams]);

  return (
    <Shell>
      <PageIntro
        label="Universities we can guide you to apply to"
        title={<>The right university<br /><em>changes everything.</em></>}
      >
        <p className="page-intro-note">
          University names are shown for identification only. Nexton does not claim any formal affiliation
          or exclusive admissions channel. Fees, intakes and scholarships are indicative — please confirm on each
          university's official website before applying.
        </p>
        <div className="directory-toolbar">
          <select data-testid="country-filter" value={country} onChange={(e) => setCountry(e.target.value)}>
            <option value="">All countries</option>
            {[...new Set(destinations.map((d) => d[0]))].map((x) => (
              <option key={x}>{x}</option>
            ))}
          </select>
        </div>
      </PageIntro>

      <div className="university-list">
        {items.map((u) => (
          <article className="university-row" key={u.id} data-testid={`university-${u.id}`}>
            <UniImage u={u} />
            <div className="uni-main">
              <span className="card-kicker">{u.country} · {u.city}</span>
              <h3>{u.name}</h3>
              <p>{u.programs}</p>
              <div className="uni-meta">
                <span><GraduationCap size={15} /> {u.degree}</span>
                <span>{u.fees}</span>
                <span>{u.intake}</span>
              </div>
            </div>
            <div className="uni-side">
              <Button to="/apply">Get guidance</Button>
            </div>
          </article>
        ))}
        {!items.length && <div className="empty">No universities match your search yet.</div>}
      </div>
    </Shell>
  );
}

/* ---------- FORM PRIMITIVES ---------- */
function FormStep({ title, subtitle, children }) {
  return (
    <div className="form-step">
      <h2>{title}</h2>
      <p>{subtitle}</p>
      <div className="field-grid">{children}</div>
    </div>
  );
}

function Field({ label, name, placeholder, value, onChange, type = "text" }) {
  return (
    <label className="field">
      <span>{label}</span>
      <input
        data-testid={`field-${name}`}
        name={name}
        type={type}
        placeholder={placeholder}
        value={value || ""}
        onChange={onChange}
      />
    </label>
  );
}


/* ---------- APPLY ---------- */
const QUALIFICATION_OPTIONS = ["Matric", "FSc / A-Levels", "Diploma", "Bachelor's", "Master's", "Other"];
const COUNTRY_OPTIONS = ["United Kingdom", "Germany", "Finland", "Italy", "Cyprus", "Turkey", "Australia", "Canada", "Ireland", "Other European Countries", "Not sure yet"];
const ENGLISH_TEST_OPTIONS = ["Not taken yet", "IELTS", "PTE", "TOEFL", "Duolingo", "MOI (Medium of Instruction)"];
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: CURRENT_YEAR + 5 - 1990 + 1 }, (_, i) => String(CURRENT_YEAR + 5 - i));

const APPLY_FIELDS = [
  { name: "full_name", label: "Full Name", placeholder: "e.g. Ayesha Khan", required: true, type: "text", autoComplete: "name" },
  { name: "contact_number", label: "Contact Number", placeholder: "0300 1234567", required: true, type: "tel", inputMode: "tel", autoComplete: "tel" },
  { name: "email", label: "Email Address", placeholder: "you@example.com", required: true, type: "email", inputMode: "email", autoComplete: "email" },
  { name: "city", label: "Current City / Town", placeholder: "Faisalabad", required: true, type: "text", autoComplete: "address-level2" },
  { name: "last_qualification", label: "Last Qualification", required: true, as: "select", options: QUALIFICATION_OPTIONS },
  { name: "institution_name", label: "Institution Name", placeholder: "e.g. University of the Punjab", required: true, type: "text" },
  { name: "cgpa_percentage", label: "CGPA / Percentage", placeholder: "e.g. 3.4 or 78%", required: true, type: "text", inputMode: "decimal" },
  { name: "year_of_passing", label: "Year of Passing", required: true, as: "select", options: YEAR_OPTIONS },
  { name: "preferred_country", label: "Preferred Country", required: true, as: "select", options: COUNTRY_OPTIONS },
  { name: "budget", label: "Yearly Budget (PKR)", placeholder: "e.g. 2500000", required: true, type: "text", inputMode: "numeric" },
  { name: "preferred_course", label: "Preferred Study Course", placeholder: "e.g. MSc Data Science", required: true, type: "text" },
  { name: "english_test", label: "English Language Test", required: false, as: "select", options: ENGLISH_TEST_OPTIONS }
];

const PK_PHONE_RE = /^(?:\+92|0092|92|0)3\d{9}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NAME_RE = /^[\p{L}\p{M}]+(?:[ .'\-][\p{L}\p{M}]+)*$/u;

function validateApply(form) {
  const errors = {};
  if (!form.full_name || !NAME_RE.test(form.full_name.trim())) errors.full_name = "Please enter your name using letters only.";
  const digits = (form.contact_number || "").replace(/[\s\-()]/g, "");
  if (!digits || (!PK_PHONE_RE.test(digits) && !/^\+?\d{7,15}$/.test(digits))) errors.contact_number = "Enter a valid mobile number, e.g. 0300 1234567";
  if (!form.email || !EMAIL_RE.test(form.email)) errors.email = "Enter a valid email address.";
  if (!form.city || form.city.trim().length < 2) errors.city = "Enter your city.";
  if (!form.last_qualification) errors.last_qualification = "Select your last qualification.";
  if (!form.institution_name || form.institution_name.trim().length < 2) errors.institution_name = "Enter your institution.";
  if (!form.cgpa_percentage || !/^\d+(\.\d{1,2})?%?$/.test(form.cgpa_percentage.trim())) errors.cgpa_percentage = "Enter CGPA (e.g. 3.4) or percentage (e.g. 78%).";
  if (!form.year_of_passing || !/^\d{4}$/.test(form.year_of_passing)) errors.year_of_passing = "Select a valid year.";
  if (!form.preferred_country) errors.preferred_country = "Select a country.";
  if (!form.budget || !/^\d+$/.test(form.budget.replace(/[\s,]/g, ""))) errors.budget = "Enter your yearly budget as a number.";
  if (!form.preferred_course || form.preferred_course.trim().length < 2) errors.preferred_course = "Enter a course or field.";
  if (!form.consent) errors.consent = "Please agree to be contacted.";
  return errors;
}

function Apply() {
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(null);
  const [serverError, setServerError] = useState("");
  const [sending, setSending] = useState(false);

  const update = (e) => {
    const { name, value, type, checked } = e.target;
    const v = type === "checkbox" ? checked : (name === "contact_number" ? value.replace(/[^0-9+\s\-()]/g, "") : value);
    setForm((f) => ({ ...f, [name]: v }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setServerError("");
    const found = validateApply(form);
    if (Object.keys(found).length) {
      setErrors(found);
      const first = Object.keys(found)[0];
      document.querySelector(`[name="${first}"]`)?.focus();
      return;
    }
    setSending(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        contact_number: form.contact_number.replace(/[\s\-()]/g, ""),
        email: form.email.trim().toLowerCase(),
        city: form.city.trim(),
        last_qualification: form.last_qualification,
        institution_name: form.institution_name.trim(),
        cgpa_percentage: form.cgpa_percentage.trim(),
        year_of_passing: form.year_of_passing,
        preferred_country: form.preferred_country,
        budget: form.budget.replace(/[\s,]/g, ""),
        preferred_course: form.preferred_course.trim(),
        english_test: form.english_test || "",
        consent: !!form.consent,
        website: form.website || ""
      };
      const r = await api.post("/applications", payload);
      setSubmitted(r.data);
    } catch (x) {
      const detail = x.response?.data?.detail;
      if (Array.isArray(detail)) {
        const next = {};
        detail.forEach((d) => { if (d.loc?.[1]) next[d.loc[1]] = d.msg; });
        setErrors(next);
        setServerError("Please correct the highlighted fields and try again.");
      } else {
        setServerError(typeof detail === "string" ? detail : "We couldn't submit your application. Please try again shortly.");
      }
    } finally {
      setSending(false);
    }
  };

  if (submitted) {
    return (
      <Shell>
        <div className="success-page">
          <div className="success-icon"><Check /></div>
          <div className="section-label">Application received</div>
          <h1>You're on your way.</h1>
          <p>Thank you, {form.full_name}. Our counselor will review your profile and contact you on <strong>{form.contact_number}</strong> shortly.</p>
          <div className="application-id">
            <span>Your application ID</span>
            <strong data-testid="application-id">{submitted.id}</strong>
          </div>
          <Button to="/">Back to home</Button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <section className="application-layout">
        <aside className="application-aside">
          <div className="section-label gold">Student application</div>
          <h1>Your next chapter<br /><em>starts here.</em></h1>
          <p>Fill in a few details and our counselors will reach out to help you choose the right destination, university and program.</p>
          <ul className="apply-perks">
            <li><Check size={15} /> Free consultation</li>
            <li><Check size={15} /> Personalized shortlist</li>
            <li><Check size={15} /> Visa & document guidance</li>
          </ul>
        </aside>

        <form className="form-panel" onSubmit={submit} data-testid="apply-form" noValidate>
          <div className="form-step">
            <h2>Tell us about you.</h2>
            <p>Your details help us match you with the right universities and scholarships.</p>
            <div className="field-grid">
              {/* honeypot - hidden from real users */}
              <input
                type="text"
                name="website"
                value={form.website || ""}
                onChange={update}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
              />
              {APPLY_FIELDS.map((f) => (
                <label className={`field ${errors[f.name] ? "field-error" : ""}`} key={f.name}>
                  <span>{f.label}{f.required && " *"}</span>
                  {f.as === "select" ? (
                    <select
                      data-testid={`field-${f.name}`}
                      name={f.name}
                      value={form[f.name] || ""}
                      onChange={update}
                      required={f.required}
                      aria-invalid={!!errors[f.name]}
                    >
                      <option value="">Select…</option>
                      {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input
                      data-testid={`field-${f.name}`}
                      name={f.name}
                      type={f.type}
                      inputMode={f.inputMode}
                      autoComplete={f.autoComplete}
                      placeholder={f.placeholder}
                      value={form[f.name] || ""}
                      onChange={update}
                      required={f.required}
                      aria-invalid={!!errors[f.name]}
                    />
                  )}
                  {errors[f.name] && <em className="field-error-msg" data-testid={`error-${f.name}`}>{errors[f.name]}</em>}
                </label>
              ))}
              <label className={`check-field ${errors.consent ? "field-error" : ""}`} data-testid="consent-wrapper">
                <input
                  type="checkbox"
                  name="consent"
                  data-testid="field-consent"
                  checked={!!form.consent}
                  onChange={update}
                />
                I agree to be contacted by Nexton and have read the privacy notice.
              </label>
              {errors.consent && <em className="field-error-msg" data-testid="error-consent">{errors.consent}</em>}
              {serverError && <div className="form-error" data-testid="application-error" role="alert">{serverError}</div>}
            </div>
          </div>
          <div className="form-actions single">
            <button type="submit" className="button" data-testid="application-submit" disabled={sending}>
              {sending ? "Submitting…" : "Submit application"} <ArrowRight size={17} />
            </button>
          </div>
        </form>
      </section>
    </Shell>
  );
}


/* ---------- LOGIN ---------- */
function Login() {
  const [register, setRegister] = useState(false);
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const nav = useNavigate();
  const loc = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post(register ? "/auth/register" : "/auth/login", form);
      const next = new URLSearchParams(loc.search).get("next");
      nav(next || (register ? "/apply" : "/dashboard"));
    } catch (x) {
      setError(x.response?.data?.detail || "Please try again");
    }
  };

  return (
    <Shell>
      <div className="auth-page">
        <div className="auth-copy">
          <div className="section-label">Your journey, together</div>
          <h1>Good guidance<br /><em>goes a long way.</em></h1>
          <p>Access your application, documents and counselor updates in one secure place.</p>
        </div>
        <form className="auth-form" onSubmit={submit}>
          <div className="section-label">{register ? "Create your profile" : "Welcome back"}</div>
          <h2>{register ? "Start your application" : "Sign in to Nexton"}</h2>
          {register && (
            <Field label="Full name" name="name" placeholder="Your full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          )}
          <Field label="Email" name="email" type="email" placeholder="you@example.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <Field label="Password" name="password" type="password" placeholder="At least 6 characters" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          {error && <div className="form-error" data-testid="auth-error">{error}</div>}
          <button className="button full" data-testid="auth-submit">
            {register ? "Create account" : "Sign in"}
            <ArrowRight size={17} />
          </button>
          <button
            type="button"
            className="text-button"
            data-testid="auth-toggle"
            onClick={() => setRegister(!register)}
          >
            {register ? "Already have an account? Sign in" : "New to Nexton? Create an account"}
          </button>
        </form>
      </div>
    </Shell>
  );
}

/* ---------- DOCUMENT PANEL ---------- */
const DOC_TYPES = [
  "Passport", "CNIC", "Photograph",
  "Matric Certificate", "Matric Transcript",
  "Intermediate Certificate", "Intermediate Transcript",
  "Bachelor's Degree", "Bachelor's Transcript",
  "Master's Degree", "Master's Transcript",
  "IELTS / PTE / TOEFL Certificate",
  "CV / Resume", "Experience Letter", "Recommendation Letter",
  "Statement of Purpose", "Bank Statement", "Other"
];

function DocumentPanel({ appId, documents, admin = false, onUpload }) {
  const [docType, setDocType] = useState(DOC_TYPES[0]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      const r = await api.post(`/applications/${appId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      onUpload && onUpload(appId, r.data);
    } catch (x) {
      setError(x.response?.data?.detail || "Upload failed — please try again");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const download = async (doc) => {
    try {
      const r = await api.get(`/applications/${appId}/documents/${doc.stored_name}`, {
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.name;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setError("Unable to download");
    }
  };

  return (
    <div className="doc-panel" data-testid={`doc-panel-${appId}`}>
      <div className="doc-panel-head">
        <span className="card-kicker">Documents · {documents?.length || 0}</span>
      </div>

      <div className="doc-list">
        {(documents || []).map((d, i) => (
          <div className="doc-item" key={`${d.stored_name}-${i}`} data-testid={`doc-item-${appId}-${i}`}>
            <FileText size={16} />
            <div className="doc-info">
              <strong>{d.doc_type || "Document"}</strong>
              <small>{d.name}</small>
            </div>
            <span className="doc-status">{d.status || "Under review"}</span>
            <button
              type="button"
              className="doc-download"
              data-testid={`doc-download-${appId}-${i}`}
              onClick={() => download(d)}
            >
              Download
            </button>
          </div>
        ))}
        {!documents?.length && (
          <div className="doc-empty">No documents uploaded yet.</div>
        )}
      </div>

      {!admin && (
        <div className="doc-upload">
          <select
            data-testid={`doc-type-${appId}`}
            value={docType}
            onChange={(e) => setDocType(e.target.value)}
            disabled={busy}
          >
            {DOC_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
          <label className="doc-file-btn">
            <Upload size={16} />
            {busy ? "Uploading…" : "Upload file"}
            <input
              type="file"
              data-testid={`doc-upload-${appId}`}
              accept=".pdf,.jpg,.jpeg,.png"
              disabled={busy}
              onChange={handleFile}
            />
          </label>
        </div>
      )}

      {error && <div className="form-error" data-testid={`doc-error-${appId}`}>{error}</div>}
    </div>
  );
}

/* ---------- DASHBOARD ---------- */
function Dashboard({ admin = false }) {
  const [user, setUser] = useState(null);
  const [apps, setApps] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        return api.get("/applications");
      })
      .then((r) => setApps(r.data))
      .catch(() => nav("/login"));
  }, [nav]);

  const update = async (id, value) => {
    await api.patch(`/applications/${id}/status`, { status: value });
    setApps((prev) =>
      prev.map((a) =>
        a.id === id
          ? { ...a, status: value, progress: Math.round(((stages.indexOf(value) + 1) / 7) * 100) }
          : a
      )
    );
  };

  const addDocument = (id, doc) => {
    setApps((prev) => prev.map((a) => a.id === id ? { ...a, documents: [...(a.documents || []), doc] } : a));
  };

  return (
    <Shell>
      <section className="dashboard">
        <div className="dashboard-head">
          <div>
            <div className="section-label">{admin ? "Counselor workspace" : "Student dashboard"}</div>
            <h1>
              {admin ? "Keep every journey moving." : <>Welcome back, <em>{user?.name?.split(" ")[0]}.</em></>}
            </h1>
          </div>
          {admin ? (
            <span className="role-pill"><ShieldCheck size={15} /> Admin portal</span>
          ) : (
            <Button to="/apply">New application</Button>
          )}
        </div>

        {admin && (
          <div className="admin-stats">
            <div><span>Active applications</span><strong>{apps.length || "—"}</strong></div>
            <div><span>Documents to review</span><strong>18</strong></div>
            <div><span>Offers this month</span><strong>24</strong></div>
            <div><span>Visa success rate</span><strong>96%</strong></div>
          </div>
        )}

        <div className="dashboard-grid">
          {apps.map((a) => (
            <article className="application-card" key={a.id} data-testid={`application-card-${a.id}`}>
              <div className="application-card-head">
                <span className="card-kicker">Application {a.id}</span>
                <span className="status-pill">{a.status}</span>
              </div>
              <h2>{a.data?.country || "Study abroad application"}</h2>
              <p>{a.data?.program || "Your counselor will help you choose the right program."}</p>
              <div className="dashboard-progress">
                <div><span>Application progress</span><strong>{a.progress}%</strong></div>
                <div className="progress"><i style={{ width: `${a.progress}%` }} /></div>
              </div>
              {admin ? (
                <div className="admin-controls">
                  <span>{a.name} · {a.email}</span>
                  <select
                    data-testid={`status-select-${a.id}`}
                    value={a.status}
                    onChange={(e) => update(a.id, e.target.value)}
                  >
                    {stages.map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="dashboard-detail">
                  <span><MessageCircle size={16} /> {a.messages?.length || 0} messages from counselor</span>
                </div>
              )}
              <DocumentPanel
                appId={a.id}
                documents={a.documents}
                admin={admin}
                onUpload={addDocument}
              />
            </article>
          ))}
          {!apps.length && (
            <div className="empty dashboard-empty">
              <Sparkles size={22} />
              <h2>Your journey starts here.</h2>
              <p>Complete your student application and your counselor workspace will appear here.</p>
              <Button to="/apply">Start application</Button>
            </div>
          )}
        </div>
      </section>
    </Shell>
  );
}

const LEADERSHIP = [
  {
    name: "Dr. Mamoona Sarwar",
    role: "Founder & CEO",
    initials: "MS",
    accent: "star",
    quote: "Guiding Your Journey, Building Your Future.",
    bio: "Dr. Mamoona Sarwar leads Nexton Education Consultant with sincerity, integrity and a deep belief in every student's potential. She has personally guided hundreds of students from Pakistan through their admissions and visa journey to leading universities across the UK, Europe, Canada and Australia."
  },
  {
    name: "Muhammad Abaid Ur Rehman",
    role: "Director",
    initials: "AR",
    accent: "compass",
    quote: "Empowering students with the right guidance, opportunities and confidence to achieve their academic dreams and build a brighter future.",
    bio: "As Director of Nexton Education Consultant, Muhammad Abaid Ur Rehman oversees strategy and student success. He is committed to building a transparent, supportive and student-first consultancy where every ambition is met with the right opportunity."
  },
  {
    name: "Eman Sarwar",
    role: "Director",
    initials: "ES",
    accent: "award",
    quote: "To inspire and empower students with honest guidance, quality opportunities, and the confidence to achieve their educational dreams and create a successful future.",
    bio: "Eman Sarwar brings warmth, discipline and a student-first mindset to Nexton. She works closely with every student to understand their ambitions and match them with the right destination, university and program — guiding each journey with honesty and care."
  }
];

function LeaderMonogram({ initials, accent }) {
  const AccentIcon = accent === "star" ? Star : accent === "compass" ? Compass : Award;
  return (
    <div className="monogram">
      <div className="monogram-grid" aria-hidden="true" />
      <div className="monogram-corner tl" aria-hidden="true" />
      <div className="monogram-corner br" aria-hidden="true" />
      <div className="monogram-initials">
        <span>{initials[0]}</span>
        <span className="gold">{initials[1]}</span>
      </div>
      <div className="monogram-badge">
        <AccentIcon size={16} />
      </div>
      <div className="monogram-ring" aria-hidden="true" />
    </div>
  );
}

const VISION_POINTS = [
  "Guiding students toward the right educational opportunities abroad",
  "Providing honest, transparent and personalized education consultancy",
  "Supporting students from their first consultation until their admission and visa journey",
  "Connecting students with suitable universities and programs according to their academic background, goals and financial situation",
  "Promoting quality education, personal growth, confidence and international exposure",
  "Working with sincerity, integrity, compassion and a strong commitment to students' dreams"
];

const MISSION_STATEMENT = "Guiding Your Journey, Building Your Future.";

/* ---------- SIMPLE PAGES ---------- */
const PAGE_IMAGES = {
  programs: {
    hero: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1200&q=80",
    fallback: "https://images.unsplash.com/photo-1497633762265-9d179a990aa6?auto=format&fit=crop&w=1200&q=80",
    gallery: [
      { url: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=800&q=80", title: "Business & Finance", caption: "MBA, Analytics, International Business" },
      { url: "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?auto=format&fit=crop&w=800&q=80", title: "Tech & Engineering", caption: "Computer Science, Data, AI, Cybersecurity" },
      { url: "https://images.unsplash.com/photo-1584515933487-779824d29309?auto=format&fit=crop&w=800&q=80", title: "Health & Life Sciences", caption: "Nursing, Public Health, Biomedical Sciences" },
      { url: "https://images.unsplash.com/photo-1558655146-9f40138edfeb?auto=format&fit=crop&w=800&q=80", title: "Design & Media", caption: "Architecture, Graphic Design, Film Studies" }
    ]
  },
  scholarships: {
    hero: "https://images.unsplash.com/photo-1627556704290-2b1f5853ff78?auto=format&fit=crop&w=1200&q=80",
    fallback: "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?auto=format&fit=crop&w=1200&q=80",
    gallery: [
      { url: "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?auto=format&fit=crop&w=800&q=80", title: "Merit Scholarships", caption: "Awarded for strong academic performance" },
      { url: "https://images.unsplash.com/photo-1507537297725-24a1c029d3ca?auto=format&fit=crop&w=800&q=80", title: "Need-Based Grants", caption: "Supporting students with financial constraints" },
      { url: "https://images.unsplash.com/photo-1543269865-cbf427effbad?auto=format&fit=crop&w=800&q=80", title: "Country-Specific Awards", caption: "DAAD, Chevening, Türkiye Bursları, Erasmus+" },
      { url: "https://images.unsplash.com/photo-1588072432836-e10032774350?auto=format&fit=crop&w=800&q=80", title: "University Waivers", caption: "25%–100% tuition reductions at partner unis" }
    ]
  },
  about: {
    hero: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=1200&q=80",
    fallback: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1200&q=80",
    gallery: []
  }
};

function SimplePage({ type }) {
  const content = {
    programs: {
      title: <>Programs built<br /><em>around your ambition.</em></>,
      label: "Programs",
      body: "From business and technology to healthcare and design, find a course that opens doors.",
      kicker: "Your advantage",
      galleryLabel: "Fields we help students apply into"
    },
    scholarships: {
      title: <>Make your next step<br /><em>more possible.</em></>,
      label: "Scholarships",
      body: "We help you discover scholarships, fee waivers and funding routes that fit your profile.",
      kicker: "Your advantage",
      galleryLabel: "Funding routes we guide students through"
    },
    about: {
      title: <>Guidance with<br /><em>a point of view.</em></>,
      label: "About Nexton",
      body: "Nexton is a Pakistan-based education consultancy built around one belief: every student deserves advice that feels personal, honest and informed.",
      kicker: "Our promise",
      galleryLabel: ""
    }
  }[type];

  const media = PAGE_IMAGES[type] || PAGE_IMAGES.about;

  return (
    <Shell>
      <PageIntro label={content.label} title={content.title} />
      <section className="simple-content">
        <div className="simple-image">
          <img
            src={media.hero}
            alt={content.label}
            onError={(e) => {
              if (media.fallback && e.target.src !== media.fallback) {
                e.target.src = media.fallback;
              }
            }}
          />
        </div>
        <div>
          <div className="section-label">{content.kicker}</div>
          <h2>{content.body}</h2>
          <p>
            Our team brings destination expertise, university relationships and patient,
            one-to-one support to every application. No shortcuts. No confusing jargon.
            Just a clear plan and a team that cares about the outcome.
          </p>
          <Button to="/apply">Talk to a counselor</Button>
        </div>
      </section>

      {media.gallery.length > 0 && (
        <section className="page-gallery" data-testid={`${type}-gallery`}>
          <div className="section-label">{content.galleryLabel}</div>
          <div className="page-gallery-grid">
            {media.gallery.map((g, i) => (
              <article className="gallery-card" key={g.title} data-testid={`${type}-gallery-${i}`}>
                <img src={g.url} alt={g.title} />
                <div className="gallery-body">
                  <h3>{g.title}</h3>
                  <p>{g.caption}</p>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {type === "about" && (
        <>
          <section className="vision-mission" data-testid="vision-mission">
            <div className="vm-mission">
              <div className="section-label gold">Our mission</div>
              <h2 className="mission-quote">
                <span className="mission-mark">"</span>
                {MISSION_STATEMENT}
                <span className="mission-mark">"</span>
              </h2>
            </div>

            <div className="vm-vision">
              <div className="vm-head">
                <div className="section-label">Our vision</div>
                <h2>What drives<br /><em>everything we do.</em></h2>
              </div>
              <ol className="vision-list">
                {VISION_POINTS.map((v, i) => (
                  <li key={v} data-testid={`vision-point-${i}`}>
                    <span className="vision-num">{String(i + 1).padStart(2, "0")}</span>
                    <strong>{v}</strong>
                  </li>
                ))}
              </ol>
            </div>
          </section>

          <section className="leadership" data-testid="leadership-section">
            <div className="section-label">Leadership</div>
            <h2>Meet the people<br /><em>behind every journey.</em></h2>
            <div className="leadership-grid">
              {LEADERSHIP.map((p, i) => (
                <article className="leader-card" key={p.name} data-testid={`leader-${i}`}>
                  <div className="leader-photo">
                    <LeaderMonogram initials={p.initials} accent={p.accent} />
                  </div>
                  <div className="leader-body">
                    <div className="card-kicker">{p.role}</div>
                    <h3>{p.name}</h3>
                    <blockquote>"{p.quote}"</blockquote>
                    <p>{p.bio}</p>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </>
      )}
    </Shell>
  );
}

/* ---------- CONTACT ---------- */
function Contact() {
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({});

  const submit = async (e) => {
    e.preventDefault();
    await api.post("/contact", form);
    setDone(true);
  };

  return (
    <Shell>
      <PageIntro
        label="Contact Nexton"
        title={<>Questions are good.<br /><em>Let's talk.</em></>}
      >
        <p>Whether you're just exploring or ready to apply, our counselors are one message away.</p>
      </PageIntro>

      <section className="contact-grid">
        <div>
          <div className="contact-item"><span>WhatsApp & phone</span><strong><a href={`tel:${NEXTON.phone_dial}`}>{NEXTON.phone_display}</a></strong></div>
          <div className="contact-item"><span>Email</span><strong><a href={`mailto:${NEXTON.email}`}>{NEXTON.email}</a></strong></div>
          <div className="contact-item">
            <span>Office</span>
            <strong>{NEXTON.address}</strong>
            <a
              href={NEXTON.map_link}
              target="_blank"
              rel="noreferrer"
              className="map-link"
              data-testid="open-in-maps"
            >
              Open in Google Maps <ArrowRight size={14} />
            </a>
          </div>
          <div className="contact-socials" data-testid="contact-socials">
            {SOCIALS.map((s) => (
              <a
                key={s.name}
                href={s.url}
                target="_blank"
                rel="noreferrer"
                aria-label={s.name}
                data-testid={`contact-social-${s.name.toLowerCase()}`}
                className="social-icon lg"
                style={{ background: s.gradient || s.color, color: "#fff" }}
              >
                {s.svg}
              </a>
            ))}
          </div>
        </div>
        <form className="contact-form" onSubmit={submit}>
          {done ? (
            <div className="success-inline" data-testid="contact-success">
              <Check />
              <h2>Message received.</h2>
              <p>We'll be in touch shortly.</p>
            </div>
          ) : (
            <>
              <Field label="Name" name="name" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <Field label="Phone" name="phone" placeholder="WhatsApp number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <Field label="Email" name="email" placeholder="you@example.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <Field label="Interested country" name="country" placeholder="Where are you thinking?" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
              <label className="field">
                <span>Message</span>
                <textarea
                  data-testid="contact-message"
                  name="message"
                  placeholder="Tell us a little about your plans"
                  value={form.message || ""}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                />
              </label>
              <button className="button" data-testid="contact-submit">
                Send message <ArrowRight size={17} />
              </button>
            </>
          )}
        </form>
      </section>

      <section className="map-section" data-testid="map-section">
        <iframe
          title="Nexton Education office location"
          src={NEXTON.map_embed}
          width="100%"
          height="440"
          style={{ border: 0, display: "block", filter: "grayscale(0.15)" }}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
        />
      </section>
    </Shell>
  );
}

/* ---------- APP ---------- */
function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/destinations" element={<Destinations />} />
        <Route path="/universities" element={<Universities />} />
        <Route path="/apply" element={<Apply />} />
        <Route path="/login" element={<Login />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/admin" element={<Dashboard admin />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/programs" element={<SimplePage type="programs" />} />
        <Route path="/scholarships" element={<SimplePage type="scholarships" />} />
        <Route path="/about" element={<SimplePage type="about" />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
